import TerminalSetup from "@/modules/PdvDesktop/pages/TerminalSetup";
import ConfigurarTerminal from "@/modules/PdvDesktop/pages/ConfigurarTerminal";
import SincronizacaoInicial from "@/modules/PdvDesktop/pages/SincronizacaoInicial";
import NotasLocais from "@/modules/PdvDesktop/pages/NotasLocais";
import PdvDashboard from "@/modules/PdvDesktop/pages/PdvDashboard";
import VendasOffline from "@/modules/PdvDesktop/pages/VendasOffline";
import PdvDesktop from "@/modules/PdvDesktop/pages/PdvDesktop";
import NovaVenda from "@/modules/PdvDesktop/pages/NovaVenda";
import ConfiguracaoImpressao from "@/modules/PdvDesktop/pages/ConfiguracaoImpressao";
import ConfiguracaoBalanca from "@/modules/PdvDesktop/pages/ConfiguracaoBalanca";
import CaixaResumo from "@/modules/PdvDesktop/pages/CaixaResumo";
import BloqueioSuporte from "@/modules/PdvDesktop/pages/BloqueioSuporte";
import PdvDesktopLayout from "@/modules/PdvDesktop/layout/PdvDesktopLayout";
import PdvDesktopAuthLayout from "@/modules/PdvDesktop/layout/PdvDesktopAuthLayout";
import Login from "@/modules/PdvDesktop/pages/Login";

const _BLOQUEIO_EMPRESA_CODES = new Set([
  "EMPRESA_BLOQUEADA",
  "ASSINATURA_VENCIDA",
  "EMPRESA_SEM_ACESSO",
  "PLANO_BLOQUEADO",
]);

const _isCodigoBloqueioEmpresa = (code) =>
  _BLOQUEIO_EMPRESA_CODES.has(String(code || "").trim().toUpperCase());

const _obterBloqueioEmpresaLocal = async () => {
  if (
    typeof window === "undefined" ||
    !window.electronAPI ||
    window.electronAPI.isDesktop !== true
  ) {
    return { bloqueado: false, code: null, motivo: "" };
  }

  try {
    const [bloqueadoRaw, code, motivo] = await Promise.all([
      window.electronAPI.config.get("terminal_bloqueado"),
      window.electronAPI.config.get("terminal_code_bloqueio"),
      window.electronAPI.config.get("terminal_motivo_bloqueio"),
    ]);

    const bloqueado =
      bloqueadoRaw === "1" ||
      bloqueadoRaw === 1 ||
      bloqueadoRaw === true ||
      bloqueadoRaw === "true";

    if (!bloqueado || !_isCodigoBloqueioEmpresa(code)) {
      return { bloqueado: false, code: null, motivo: "" };
    }

    return { bloqueado: true, code: code || null, motivo: motivo || "" };
  } catch (_) {
    return { bloqueado: false, code: null, motivo: "" };
  }
};

/**
 * Guard de autenticação básica: exige apenas token salvo.
 * Usado na tela de configuração do terminal (pré-operação).
 */
const _guardPdvAuth = async (to, from, next) => {
  if (typeof window === "undefined" || !window.electronAPI) return next();
  try {
    const token = await window.electronAPI.config.get("api_token");
    if (!token) return next({ name: "pdvDesktopLogin" });
  } catch (_) {
    return next({ name: "pdvDesktopLogin" });
  }
  next();
};

/**
 * Guard completo para rotas operacionais do PDV Desktop.
 * Exige token válido E terminal vinculado/validado.
 * - Sem token → redireciona para login
 * - Sem terminal configurado → redireciona para setup de terminal
 * Aplica-se a cada rota operacional individualmente para cobrir
 * também navegações intra-layout (onde o guard do pai não dispara).
 */
const _guardTerminalConfigured = async (to, from, next) => {
  if (typeof window === "undefined" || !window.electronAPI) return next();
  try {
    const token = await window.electronAPI.config.get("api_token");
    if (!token) return next({ name: "pdvDesktopLogin" });

    const bloqueio = await _obterBloqueioEmpresaLocal();
    if (bloqueio.bloqueado && to.name !== "pdvDesktopBloqueio") {
      return next({ name: "pdvDesktopBloqueio" });
    }

    const terminal = await window.electronAPI.terminal.getInfo();
    if (!terminal || !terminal.validado) {
      return next({ name: "pdvDesktopConfiguracaoTerminal" });
    }

    const [deviceId, configuracaoNfce] = await Promise.all([
      window.electronAPI.config.get("device_id"),
      window.electronAPI.configuracaoNfce.buscarConfiguracao(),
    ]);

    const temNumeracaoValida =
      Number(configuracaoNfce?.proximaNota ?? 0) > 0 ||
      Number(configuracaoNfce?.proximaVenda ?? 0) > 0;

    const fiscalInvalido =
      !terminal?.uuid_terminal ||
      !deviceId ||
      !terminal?.id_empresa ||
      !terminal?.id_configuracao ||
      !configuracaoNfce?.id_configuracao ||
      Number(configuracaoNfce?.modelo ?? 65) !== 65 ||
      !configuracaoNfce?.ambiente ||
      !configuracaoNfce?.serie ||
      !temNumeracaoValida;

    if (fiscalInvalido) {
      return next({ name: "pdvDesktopConfiguracaoTerminal" });
    }
  } catch (_) {
    return next({ name: "pdvDesktopLogin" });
  }
  next();
};

export default [
  // ── Login do PDV Desktop (layout limpo, sem topbar/sidebar) ──────────
  {
    path: "/pdv-desktop/login",
    component: PdvDesktopAuthLayout,
    meta: { semLayout: true, public: true },
    children: [
      {
        name: "pdvDesktopLogin",
        path: "",
        component: Login,
        meta: { title: "Acessar PDV Desktop", semLayout: true, public: true },
      },
    ],
  },

  // ── Setup / vinculação do terminal (layout limpo, sem topbar) ────────
  {
    path: "/pdv-desktop/configuracao-terminal",
    component: PdvDesktopAuthLayout,
    meta: { semLayout: true },
    beforeEnter: _guardPdvAuth,
    children: [
      {
        name: "pdvDesktopConfiguracaoTerminal",
        path: "",
        component: ConfigurarTerminal,
        meta: { title: "Configuração do Terminal PDV", semLayout: true },
      },
    ],
  },

  // ── Tela de bloqueio/suporte ───────────────────────────────────────────
  {
    path: "/pdv-desktop/bloqueio",
    component: PdvDesktopAuthLayout,
    meta: { semLayout: true },
    beforeEnter: _guardPdvAuth,
    children: [
      {
        name: "pdvDesktopBloqueio",
        path: "",
        component: BloqueioSuporte,
        meta: { title: "PDV bloqueado", semLayout: true },
      },
    ],
  },

  // ── Fluxo de setup / sincronização (sem layout, rotas legadas) ────────
  {
    name: "pdvTerminalSetup",
    path: "/pdv/terminal-setup",
    component: TerminalSetup,
    meta: { title: "Configuração do Terminal PDV", semLayout: true },
  },
  {
    name: "pdvSincronizacaoInicial",
    path: "/pdv/sincronizacao-inicial",
    component: SincronizacaoInicial,
    meta: { title: "Sincronizando...", semLayout: true },
  },
  {
    name: "pdvNotasLocais",
    path: "/pdv/notas-locais",
    component: NotasLocais,
    meta: { title: "Notas Fiscais Locais", semLayout: true },
  },
  {
    name: "pdvDashboard",
    path: "/pdv/dashboard",
    component: PdvDashboard,
    meta: { title: "Painel PDV", semLayout: true },
  },
  {
    name: "pdvVendasOffline",
    path: "/pdv/vendas-offline",
    component: VendasOffline,
    meta: { title: "Vendas do PDV", semLayout: true },
  },
  // ── PDV Desktop legado (mantém compatibilidade; não alterar) ──────────
  {
    name: "pdvVenda",
    path: "/pdv/venda/:id?",
    component: PdvDesktop,
    meta: { title: "PDV Desktop (legado)", semLayout: true },
  },

  // ── Novo módulo PDV Desktop com layout próprio ────────────────────────
  {
    path: "/pdv-desktop",
    component: PdvDesktopLayout,
    meta: { semLayout: true },
    beforeEnter: _guardTerminalConfigured,
    children: [
      {
        name: "pdvDesktopDashboard",
        path: "dashboard",
        component: PdvDashboard,
        beforeEnter: _guardTerminalConfigured,
        meta: { title: "Painel PDV Desktop", semLayout: true },
      },
      {
        name: "pdvDesktopNovaVenda",
        path: "nova-venda",
        component: NovaVenda,
        beforeEnter: _guardTerminalConfigured,
        meta: { title: "Nova Venda", semLayout: true },
      },
      {
        name: "pdvDesktopVendasOffline",
        path: "vendas-offline",
        component: VendasOffline,
        beforeEnter: _guardTerminalConfigured,
        meta: { title: "Vendas do PDV", semLayout: true },
      },
      {
        name: "pdvDesktopConfiguracaoImpressao",
        path: "configuracao-impressao",
        component: ConfiguracaoImpressao,
        beforeEnter: _guardTerminalConfigured,
        meta: { title: "Configuração de Impressão", semLayout: true },
      },
      {
        name: "pdvDesktopConfiguracaoBalanca",
        path: "configuracao-balanca",
        component: ConfiguracaoBalanca,
        beforeEnter: _guardTerminalConfigured,
        meta: { title: "Configuração da Balança", semLayout: true },
      },
      {
        name: "pdvDesktopCaixaResumo",
        path: "caixa",
        component: CaixaResumo,
        beforeEnter: _guardTerminalConfigured,
        meta: { title: "Resumo de Caixa", semLayout: true },
      },
      // Redireciona /pdv-desktop → /pdv-desktop/dashboard
      {
        path: "",
        redirect: { name: "pdvDesktopDashboard" },
      },
    ],
  },
];
